<template>
  <div class="border rounded-lg p-3 mb-2" :class="planBorderClass">
    <div class="flex items-center gap-2 mb-2">
      <TaskStatusBadge :status="plan.status" />
      <span v-if="plan.recipeCode" class="text-xs font-mono text-surface-500">
        {{ plan.recipeCode }}
      </span>
      <div class="flex-1"></div>
      <Button
        v-if="plan.status === 'failed'"
        label="Rerun"
        icon="pi pi-replay"
        size="small"
        severity="danger"
        variant="outlined"
        :loading="rerunning"
        @click="handleRerun"
      />
    </div>
    <div v-for="node in plan.nodes" :key="node.id" class="ml-3 mb-1.5">
      <div class="flex items-center gap-2">
        <TaskStatusBadge :status="node.status" />
        <span class="text-sm text-surface-300">{{ formatTaskType(node.taskType) }}</span>
        <div class="flex-1"></div>
        <Button
          v-if="node.status === 'failed'"
          label="Mark as OK"
          icon="pi pi-check"
          size="small"
          severity="secondary"
          variant="outlined"
          :loading="overridingNodeId === node.id"
          @click="handleOverride(node.id)"
        />
      </div>
      <div v-if="node.error" class="ml-6 text-xs text-red-400 mt-0.5">
        {{ node.error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import type { WorkflowTaskPlanDto } from '@/api/generated'
import { useApiClient } from '@/api/apiClient'
import TaskStatusBadge from './TaskStatusBadge.vue'

const props = defineProps<{
  plan: WorkflowTaskPlanDto
}>()

const emit = defineEmits<{
  rerunComplete: []
}>()

const { api } = useApiClient()
const rerunning = ref(false)
const overridingNodeId = ref<string | null>(null)

async function handleRerun() {
  rerunning.value = true
  try {
    await api.taskPlanControllerRerunPlan({ path: { planId: props.plan.id } })
    emit('rerunComplete')
  } finally {
    rerunning.value = false
  }
}

async function handleOverride(nodeId: string) {
  overridingNodeId.value = nodeId
  try {
    await api.taskPlanControllerOverrideNode({ path: { planId: props.plan.id, nodeId } })
    emit('rerunComplete')
  } finally {
    overridingNodeId.value = null
  }
}

const planBorderClass = computed(() => {
  switch (props.plan.status) {
    case 'completed':
      return 'border-green-500/30'
    case 'failed':
      return 'border-red-500/30'
    case 'running':
      return 'border-blue-500/30'
    default:
      return 'border-surface-700'
  }
})

function formatTaskType(taskType: string): string {
  return taskType
    .split('-')
    .map(w => (w === 'qa' ? 'QA' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ')
}
</script>
